import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Shirt, 
  ShoppingCart, 
  ArrowLeft, 
  CheckCircle,
  Star,
  Heart
} from 'lucide-react';
import axios from 'axios';
import AvatarViewer from './AvatarViewer';
import { API_BASE } from '@/config/api';

interface Garment {
  garment_id: string;
  name: string;
  category: string;
  sizes: string[];
  colors: string[];
  price: number;
  image_url: string;
}

type TryOnPageProps = Record<string, never>;

const TryOnPage: React.FC<TryOnPageProps> = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [selectedGarment, setSelectedGarment] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [isApplyingGarment, setIsApplyingGarment] = useState(false);

  const { data: garments, isLoading: garmentsLoading } = useQuery<{ garments: Garment[] }>(
    ['garments'],
    async () => {
      const apiUrl = API_BASE;
      const response = await axios.get(`${apiUrl}/api/garments`);
      return response.data;
    }
  );

  const { data: userMeasurements } = useQuery(
    ['measurements', userId],
    async () => {
      const apiUrl = API_BASE;
      const response = await axios.get(`${apiUrl}/api/measurements/${userId}`);
      return response.data;
    },
    {
      enabled: !!userId,
    }
  );

  const handleTryOn = async () => {
    if (!selectedGarment || !selectedSize || !selectedColor) return;

    setIsApplyingGarment(true);
    try {
      const apiUrl = API_BASE;

      const response = await axios.post(`${apiUrl}/api/try-on`, {
        user_id: userId,
        garment_id: selectedGarment,
        size: selectedSize,
        color: selectedColor
      });

      // In a real implementation, this would update the 3D model
      console.log('Garment applied:', response.data);
      
    } catch (_error) {
      console.error('Error applying garment:', error);
    } finally {
      setIsApplyingGarment(false);
    }
  };

  const getRecommendedSize = (garment: Garment) => {
    if (!userMeasurements) return 'M';
    
    // Simple size recommendation logic
    const chest = userMeasurements.measurements.chest;
    if (chest < 36) return 'S';
    if (chest < 40) return 'M';
    if (chest < 44) return 'L';
    return 'XL';
  };

  if (garmentsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Garments</h2>
          <p className="text-gray-600">Preparing your virtual wardrobe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(`/results/${userId}`)}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Results
            </button>
            <h1 className="text-4xl font-bold text-gray-900">Virtual Try-On</h1>
          </div>
          <p className="text-xl text-gray-600 text-center">
            Try on clothes virtually with your personalized 3D avatar
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 3D Avatar with Garment */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="card"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shirt className="w-6 h-6" />
              Your Avatar
            </h2>
            <div className="canvas-container">
              <AvatarViewer meshUrl={userMeasurements?.mesh_url} />
            </div>
            {selectedGarment && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Garment applied successfully!</span>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Garment Selection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Available Garments</h3>
              <div className="grid grid-cols-1 gap-4">
                {garments?.garments.map((garment) => (
                  <motion.div
                    key={garment.garment_id}
                    whileHover={{ scale: 1.02 }}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedGarment === garment.garment_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedGarment(garment.garment_id);
                      setSelectedSize(getRecommendedSize(garment));
                      setSelectedColor(garment.colors[0]);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{garment.name}</h4>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">4.8</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{garment.category}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-blue-600">${garment.price}</span>
                      <button className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                        <Heart className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Size and Color Selection */}
            {selectedGarment && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Customize Your Fit</h3>
                
                {/* Size Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Size
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {garments?.garments
                      .find(g => g.garment_id === selectedGarment)
                      ?.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`py-2 px-4 rounded-lg border text-sm font-medium transition-all duration-200 ${
                            selectedSize === size
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Color Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <div className="flex gap-2">
                    {garments?.garments
                      .find(g => g.garment_id === selectedGarment)
                      ?.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                            selectedColor === color
                              ? 'border-blue-500 ring-2 ring-blue-200'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                  </div>
                </div>

                {/* Try On Button */}
                <button
                  onClick={handleTryOn}
                  disabled={isApplyingGarment}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isApplyingGarment ? 'Applying...' : 'Try On Garment'}
                </button>
              </motion.div>
            )}

            {/* Add to Cart */}
            {selectedGarment && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Ready to Purchase?</h3>
                <p className="text-gray-600 mb-4">
                  Love how it looks? Add this garment to your cart and get it delivered to your door.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={() => navigate('/checkout')}
                    className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Add to Cart
                  </button>
                  <button className="btn-outline flex-1">
                    Save for Later
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TryOnPage; 